import React, { useEffect, useCallback } from 'react';
import { useBasket } from '~/features/basket';
import { Modules, P170AviouPage as P170AviouPageType } from '~/lib';
import { Variant } from '~/lib/data-contract/salesforce';
import { ProductEventData } from '~/shared/hooks/useEvents/models';
import { AviouScript } from '~/shared/scripts/Aviou';
import { notifyError } from '~/shared/utils/errorBoundary/toast';
import { useTranslation } from '~/shared/utils/translation';
import { DynamicBlockList } from '~/templates/blocks';

type CatalogueItem = {
    product_id: string;
    quantity: number;
    stock: string;
    Brand: string;
    EAN: string;
    product_name: string;
    position: number;
    price_without_currency: string;
    Category: string;
};

declare global {
    interface Window {
        catalogueAddToBasket?: (basketItems: CatalogueItem[]) => void;
    }
}

const DEFAULT_QUANTITY = 1;

export const P170AviouPage = ({
    aviouId,
    aviouUrl,
    pageElements,
    bottomPageElements,
}: P170AviouPageType) => {
    const { addItemToBasket, toggleMiniBasket } = useBasket();
    const { translate } = useTranslation();

    const handleAddToBasket = useCallback(
        (basketItems: CatalogueItem[]) => {
            if (basketItems.length === 0) return;

            const [item] = basketItems;
            if (item.stock !== 'In Stock') {
                notifyError(new Error(translate('basket.itemSoldOut')));
                return;
            }

            const payload: Variant = {
                productId: item.product_id,
                DEFAULT_QUANTITY,
                // Because we return early when item.stock !== 'In Stock',
                // the product will always be orderable when constructing this payload.
                orderable: true,
            };

            const eventData: ProductEventData = {
                brand: item.Brand,
                color: '',
                ean: item.EAN,
                id: item.product_id,
                name: item.product_name,
                position: 0,
                price: Number(item.price_without_currency),
                category: item.Category,
                freightGroup: '',
                fullfillment: '',
                quantity: DEFAULT_QUANTITY,
                rating: '',
                serie: '',
                variant: '',
                ratingCount: 0,
                ratingValue: 0,
            };

            toggleMiniBasket(true);
            addItemToBasket(eventData, payload, DEFAULT_QUANTITY);
        },
        [addItemToBasket],
    );

    useEffect(() => {
        window.catalogueAddToBasket = handleAddToBasket;
    }, [handleAddToBasket]);

    return (
        <>
            <AviouScript />
            <DynamicBlockList elements={pageElements as Modules[]} />
            <av-catalogue url={aviouUrl} id={aviouId}></av-catalogue>
            <DynamicBlockList elements={bottomPageElements as Modules[]} />
        </>
    );
};
